/* eslint-disable lingui/no-unlocalized-strings */
import { WalletLayout } from '@/components/layouts/wallet-layout';
import { useLocale } from '@/i18n/lingui';
import { useClientStyle } from '@/theme/emotion';
import { theme } from '@/theme/theme';
import { withEmotionCache } from '@emotion/react';
import {
  CssBaseline,
  GlobalStyles,
  ThemeProvider,
  unstable_useEnhancedEffect as useEnhancedEffect,
} from '@mui/material';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
} from '@remix-run/react';
import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix';
import React from 'react';
import { SWRConfig } from 'swr';
import { Paths } from './constants/paths';

export const meta: MetaFunction = (args) => {
  const title = {
    [Paths.Home]: 'Instant Games',
    [Paths.About]: 'About',
    [Paths.Frogs]: 'Frogs',
    [Paths.LiveGames]: 'Live Games',
    [Paths.MyBets]: 'My Bets',
    [Paths.Terms]: 'Terms and Conditions',
  }[args.location.pathname];

  const suffix = '$FG - Frog Games - Real Frogs. Real Games. Real Fun';

  return [
    { title: [title, suffix].filter(Boolean).join(' | ') },
    {
      name: 'description',
      content:
        'Frog Games - Hungry frogs compete in daily games for tempting prizes - live streamed on X. Real Frogs. Real Games. Real Fun - $FG Token on Solana.',
    },
  ];
};

export const Layout = withEmotionCache(
  ({ children }: { children: React.ReactNode }, emotionCache) => {
    const locale = useLocale();
    const clientStyle = useClientStyle();

    // Only executed on client
    useEnhancedEffect(() => {
      // re-link sheet container
      emotionCache.sheet.container = document.head;
      // re-inject tags
      const tags = emotionCache.sheet.tags;
      emotionCache.sheet.flush();
      tags.forEach((tag) => {
        (emotionCache.sheet as any)._insertTag(tag);
      });
      // reset cache to reapply global styles
      clientStyle.reset();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <html lang={locale}>
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="theme-color" content={theme.palette.secondary.main} />
          <link rel="icon" type="image/svg+xml" href="/favicon.svg" />

          <Meta />
          <Links />
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link
            rel="preconnect"
            href="https://fonts.gstatic.com"
            crossOrigin=""
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap"
            rel="stylesheet"
          />
          <script
            async
            defer
            src="https://platform.twitter.com/widgets.js"></script>
          <meta
            name="emotion-insertion-point"
            content="emotion-insertion-point"
          />

          {import.meta.env.VITE_GTAG && (
            <>
              <script
                async
                src={`https://www.googletagmanager.com/gtag/js?id=${
                  import.meta.env.VITE_GTAG
                }`}></script>
              <script>
                {`window.dataLayer = window.dataLayer || []; function gtag(){window.dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${
                  import.meta.env.VITE_GTAG
                }');`}
              </script>
            </>
          )}
        </head>
        <body suppressHydrationWarning>
          {children}
          <ScrollRestoration />
          <Scripts />
        </body>
      </html>
    );
  }
);

export default withSentry(function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          body: {
            touchAction: 'pan-x pan-y',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            textSizeAdjust: 'none',
            lineHeight: 5 / 4,
          },
          svg: {
            lineHeight: 0,
            '&.arrow': {
              transform: 'rotate(0deg)',
              transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

              '&.open': {
                transform: 'rotate(180deg)',
              },
            },
          },
        }}
      />
      <SWRConfig
        value={{
          // refreshWhenOffline: false,
          // refreshWhenHidden: false,
          // refreshInterval: 0,
          keepPreviousData: true,
        }}>
        <WalletLayout>
          <Outlet />
        </WalletLayout>
      </SWRConfig>
    </ThemeProvider>
  );
});

export const ErrorBoundary = () => {
  const error = useRouteError();

  captureRemixErrorBoundaryError(error);

  if (isRouteErrorResponse(error)) {
    return (
      <div>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </div>
    );
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack tgame is:</p>
        <pre>{error.stack}</pre>
      </div>
    );
  }

  return <h1>Unknown Error</h1>;
};
